import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', action: () => scrollToSection('about') },
    { name: 'Projects', action: () => scrollToSection('projects') },
    { name: 'Contact', action: () => scrollToSection('contact') }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'py-2' : 'py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`relative bg-white shadow-lg rounded-2xl transition-all duration-300 ${
          isScrolled ? 'shadow-xl' : 'shadow-lg'
        }`}>
          <div className="flex items-center justify-between h-16 px-6">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3">
              <motion.img
                whileHover={{ scale: 1.05 }}
                src="/logo CDO Nepal USA (1).jpg"
                alt="CDO Nepal USA"
                className="h-12 w-auto object-contain"
              />
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="text-xl font-bold text-gray-800 hidden sm:block"
              >
                CDO Nepal USA
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {item.path ? (
                    <Link
                      to={item.path}
                      className="text-gray-700 hover:text-amber-600 transition-colors duration-300 relative group font-medium"
                    >
                      {item.name}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-600 to-yellow-600 transition-all duration-300 group-hover:w-full"></span>
                    </Link>
                  ) : (
                    <button
                      onClick={item.action}
                      className="text-gray-700 hover:text-amber-600 transition-colors duration-300 relative group font-medium"
                    >
                      {item.name}
                      <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-amber-600 to-yellow-600 transition-all duration-300 group-hover:w-full"></span>
                    </button>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Donate Button */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="hidden md:block"
            >
              <button
                onClick={() => scrollToSection('donate')}
                className="relative px-6 py-2 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/25"
              >
                <span className="relative z-10">Donate</span>
              </button>
            </motion.div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-700 hover:text-amber-600 transition-colors duration-300"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                </svg>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="md:hidden bg-white border-t border-gray-200 rounded-b-2xl"
              >
                <div className="px-4 py-6 space-y-4">
                  {navItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      {item.path ? (
                        <Link
                          to={item.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block text-gray-700 hover:text-amber-600 transition-colors duration-300 py-2 font-medium"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <button
                          onClick={item.action}
                          className="block text-gray-700 hover:text-amber-600 transition-colors duration-300 py-2 text-left w-full font-medium"
                        >
                          {item.name}
                        </button>
                      )}
                    </motion.div>
                  ))}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.5 }}
                    className="pt-4 border-t border-gray-200"
                  >
                    <button
                      onClick={() => {
                        scrollToSection('donate');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full px-6 py-3 bg-gradient-to-r from-amber-600 to-yellow-600 text-white font-semibold rounded-lg btn-shine transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-amber-500/25"
                    >
                      Donate Now
                    </button>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.nav>
  );
};

export default Navbar; 